import { Fragment } from 'react'
import classNames from 'classnames'

import { RequestWizardDraft } from '../../../context'
import {
  useInitData,
  useMenuCollapsedState,
  useModals,
  useScreen,
} from '../../../hooks'
import { CompanyName } from '../../Company'
import { withDraftProviders } from '../../Draft'
import { EmailDraftProvider } from '../../Email'
import { Header, HeaderSeparator } from '../../Header'
import { RequestDraftProvider } from '../../Request'
import { UserMenu as UserMenuComponent } from '../../User'
import { LayoutHeaderAddEntityButton } from '../LayoutHeaderAddEntityButton'
import { LayoutHeaderMenuToggle } from '../LayoutHeaderMenuToggle'
import { LayoutHeaderSearchButton } from '../LayoutHeaderSearchButton'
import { LayoutHelp } from '../LayoutHelp'
import { LayoutSearch } from '../LayoutSearch'

import {
  LAYOUT_HEADER_EMAIL_DRAFT_PREFIX,
  LAYOUT_HEADER_MODALS_CONFIG,
  LAYOUT_HEADER_REQUEST_DRAFT_PREFIX,
  LAYOUT_HEADER_REQUEST_WIZARD_DRAFT_PREFIX,
} from './LayoutHeader.constants'
import {
  useLayoutHeaderElementsVisibility,
  useLayoutHeaderUserMenuWidth,
} from './LayoutHeader.hooks'

import * as styles from './LayoutHeader.module.css'

function LayoutHeader({
  AddEntityButton = LayoutHeaderAddEntityButton,
  Help = LayoutHelp,
  MenuToggle = LayoutHeaderMenuToggle,
  Search = LayoutSearch,
  SearchButton = LayoutHeaderSearchButton,
  UserMenu = UserMenuComponent,
  className = styles.LayoutHeader,
  isOpenSearch,
  onSetOpenSearch,
}) {
  const { authorized } = useInitData()

  const [collapsed] = useMenuCollapsedState()

  const { isMobileScreenSize, width: screenWidth } = useScreen()

  const { userMenuRef, userMenuWidth } = useLayoutHeaderUserMenuWidth()

  const {
    hideCompanyAndAddButton,
    hideHelpButton,
    hideSearchButton,
    hideUserMenu,
  } = useLayoutHeaderElementsVisibility({
    collapsed,
    screenWidth,
    userMenuWidth,
  })

  const { modals, ...modalHandlers } = useModals(LAYOUT_HEADER_MODALS_CONFIG)

  const headerClassName = classNames(className, {
    [styles.collapsed]: collapsed,
    [styles.expanded]: !collapsed,
    [styles.searchOpen]: isOpenSearch,
  })

  const userMenuClassName = classNames(styles.userMenu, {
    [styles.hidden]: userMenuWidth > 0 && hideUserMenu,
  })

  return authorized ? (
    <Header className={headerClassName}>
      {isMobileScreenSize && isOpenSearch ? (
        <>
          {Search ? (
            <Search className={styles.searchFull} isOpenSearch={isOpenSearch} />
          ) : null}
          {SearchButton ? (
            <SearchButton
              isOpenSearch={isOpenSearch}
              onSetOpenSearch={onSetOpenSearch}
            />
          ) : null}
        </>
      ) : (
        <>
          {MenuToggle ? (
            <>
              <MenuToggle className={styles.menuToggle} />
              <HeaderSeparator
                className={classNames(styles.headerSeparator, {
                  [styles.headerSeparatorPlaceholder]: hideCompanyAndAddButton,
                })}
              />
            </>
          ) : null}

          {hideCompanyAndAddButton ? null : <CompanyName />}
          {Search ? (
            <Search className={styles.search} isOpenSearch={isOpenSearch} />
          ) : null}
          {AddEntityButton && !hideCompanyAndAddButton ? (
            <AddEntityButton modalHandlers={modalHandlers} />
          ) : null}
          {SearchButton && isMobileScreenSize && !hideSearchButton ? (
            <SearchButton
              isOpenSearch={isOpenSearch}
              onSetOpenSearch={onSetOpenSearch}
            />
          ) : null}
          {Help && !hideHelpButton ? <Help /> : null}
          {UserMenu ? (
            <UserMenu className={userMenuClassName} ref={userMenuRef} />
          ) : null}
          {modals.map(([id, element]) => (
            <Fragment key={id}>{element}</Fragment>
          ))}
        </>
      )}
    </Header>
  ) : null
}

export default withDraftProviders(LayoutHeader, [
  { Provider: EmailDraftProvider, prefix: LAYOUT_HEADER_EMAIL_DRAFT_PREFIX },
  {
    Provider: RequestDraftProvider,
    prefix: LAYOUT_HEADER_REQUEST_DRAFT_PREFIX,
  },
  {
    DraftContext: RequestWizardDraft,
    Provider: RequestDraftProvider,
    prefix: LAYOUT_HEADER_REQUEST_WIZARD_DRAFT_PREFIX,
  },
])
